import React from 'react';
import { Box, Drawer, IconButton, Popover } from '@mui/material';
import { Close } from '@mui/icons-material';
import { AgronodTypography } from '@agronod/mui-components';
import { useDeviceCapabilities } from '@/hooks/useDeviceCapabilities';

export interface PopoverOrSheetProps {
  /** Whether the panel is shown. */
  open: boolean;
  onClose: () => void;
  /** Element the popover anchors to on wide viewports; unused for the bottom sheet. */
  anchorEl: HTMLElement | null;
  /** Heading above the content, next to the close button. */
  title: string;
  /** Panel body. It scrolls on its own so heading and close button stay put. */
  children: React.ReactNode;
  /** Popover width limit in px on wide viewports. Defaults to 420. */
  maxWidth?: number;
}

const DEFAULT_MAX_WIDTH = 420;

/**
 * Click-revealed panel: a popover with heading, close button and its own
 * scroll on wide viewports, a bottom sheet on compact ones (layout axis from
 * `useDeviceCapabilities`). The shared container behind `InfoTip` and the
 * locked-tag explanation, so every click-to-reveal surface behaves alike
 * (AG-266, AG-272).
 *
 * Clicks inside never propagate: portal events bubble through the React tree,
 * and the host usually sits in a clickable row or an Autocomplete.
 */
const PopoverOrSheet: React.FC<PopoverOrSheetProps> = ({
  open,
  onClose,
  anchorEl,
  title,
  children,
  maxWidth = DEFAULT_MAX_WIDTH,
}) => {
  const { isCompactViewport } = useDeviceCapabilities();

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        px: 3,
        pt: 2,
        pb: isCompactViewport ? 4 : 2.5,
        maxWidth: isCompactViewport ? '100%' : maxWidth,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1, flexShrink: 0 }}>
        {/* The phone sheet is a reading surface across the full width: body1
            text under an 18 px heading. The desktop popover stays compact. */}
        <AgronodTypography
          variant={isCompactViewport ? 'subtitle1' : 'body2'}
          sx={{ fontWeight: 600, pt: isCompactViewport ? '2px' : '4px' }}
        >
          {title}
        </AgronodTypography>
        <IconButton size="small" aria-label="Stäng" onClick={onClose} sx={{ width: 28, height: 28 }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {/* The body scrolls on its own so the heading and close button stay put. */}
      <Box sx={{ overflowY: 'auto', minHeight: 0 }}>{children}</Box>
    </Box>
  );

  return isCompactViewport ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // A Drawer sits at zIndex.drawer, below dialogs (zIndex.modal); inside
      // one (the share dialog, the requirement dialog) the sheet opened
      // behind it. Same level as a modal: the later-opened one is on top.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      slotProps={{
        paper: {
          onClick: stop,
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' },
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ width: 32, height: 4, bgcolor: 'grey.300', borderRadius: 2, mx: 'auto', mt: 1.5, mb: 0.5, flexShrink: 0 }} />
      {content}
    </Drawer>
  ) : (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={stop}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          onClick: stop,
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '60vh',
            borderRadius: 3,
            mt: 1,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      }}
    >
      {content}
    </Popover>
  );
};

export default PopoverOrSheet;
